// Central site configuration: brand, nav, external links, and shared content.

export const BRAND = {
  name: "ETAP",
  tagline: "Make estimated taxes invisible.",
  logo: "/brand/ETAP_Logo_Navy.svg",
  logoWhite: "/brand/ETAP_Logo_White.svg",
  ogLogo: "/brand/ETAP_Logo_Navy_3000.png",
};

// Live product app (login points here).
export const LIVE_APP_URL = "https://etap2026-production.up.railway.app";

export const NAV_LINKS = [
  { label: "How It Works", to: "/how-it-works" },
  { label: "For Contractors", to: "/contractors" },
  { label: "For Agencies", to: "/agencies" },
  { label: "Pricing", to: "/pricing" },
  { label: "FAQ", to: "/faq" },
];

export const FOOTER_COLUMNS = [
  {
    title: "Product",
    links: [
      { label: "How It Works", to: "/how-it-works" },
      { label: "For Contractors", to: "/contractors" },
      { label: "For Agencies", to: "/agencies" },
      { label: "Pricing", to: "/pricing" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "FAQ", to: "/faq" },
      { label: "Get Started", to: "/get-started" },
      { label: "Accessibility", to: "/accessibility" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Cookie Policy", to: "/cookie-policy" },
      { label: "Terms", to: "/terms" },
    ],
  },
];

// Persona imagery from the design blueprint.
export const IMAGERY = {
  contractor:
    "https://images.unsplash.com/photo-1546514714-df0ccc50d7bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxmcmVlbGFuY2UlMjBkZXNpZ25lciUyMGxhcHRvcHxlbnwwfHx8fDE3ODUyNjU1OTF8MA&ixlib=rb-4.1.0&q=85",
  agency:
    "https://images.unsplash.com/photo-1556761175-4b46a572b786?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhY2NvdW50aW5nJTIwdGVhbSUyMG9mZmljZXxlbnwwfHx8fDE3ODUyNjU1OTJ8MA&ixlib=rb-4.1.0&q=85",
  texture:
    "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGNsZWFuJTIwbGlnaHQlMjB0ZXh0dXJlfGVufDB8fHx8MTc4NTI2NTU5Mnww&ixlib=rb-4.1.0&q=85",
};

// FAQ content. Grounded strictly in product truths. Reused for the FAQ page,
// its FAQPage JSON-LD, and inline subsections.
export const FAQ_GROUPS = [
  {
    id: "getting-started",
    title: "Getting Started",
    items: [
      {
        q: "What is ETAP in one sentence?",
        a: "ETAP watches for income on your linked account, then routes the percentage you choose toward federal and state estimated taxes, automatically, so you stop doing the math by hand.",
      },
      {
        q: "Who is ETAP built for?",
        a: "Independent contractors, freelancers, and gig workers who owe quarterly estimated taxes, plus the agencies and accountants who manage many of those clients at once.",
      },
      {
        q: "Do I choose how much gets routed?",
        a: "Yes. You set your federal and state percentages. Every routed amount is based on the percentage you choose, and you can change it whenever your situation changes.",
      },
      {
        q: "Is there an app?",
        a: "ETAP is mobile first and available on iOS today. An Android app is not available yet. You can also use ETAP from your browser.",
      },
    ],
  },
  {
    id: "security-trust",
    title: "Security and Trust",
    items: [
      {
        q: "Is my bank connection safe?",
        a: "Bank access is read only through Plaid. ETAP never sees or stores your bank login credentials. We read transactions so we can detect income, nothing more.",
      },
      {
        q: "Who holds my money?",
        a: "Nobody in between. ETAP never holds your funds. Money moves from your linked account to tax authorities through authorized payment rails. ETAP is not a bank and not a custodian.",
      },
      {
        q: "Can I see every transfer?",
        a: "Yes. Every split, fee, and transfer is recorded and auditable on your dashboard, with the fee shown on each transaction record.",
      },
      {
        q: "Do I have to give consent?",
        a: "Always. No money moves without your explicit, recorded consent, and you can withdraw consent at any time. Withdrawal takes effect immediately.",
      },
    ],
  },
  {
    id: "money-movement",
    title: "Money Movement",
    items: [
      {
        q: "What happens when a deposit arrives?",
        a: "ETAP detects the incoming payment, calculates your chosen set aside percentage, and routes that amount toward your federal and state estimated taxes. The rest stays in your account.",
      },
      {
        q: "What if a deposit is not income?",
        a: "You stay in control. You can mark deposits that are not income, such as transfers or refunds, so nothing is routed on money that should not be counted.",
      },
      {
        q: "Can I pause it?",
        a: "Yes. You can pause routing at any time. When paused, ETAP keeps watching so your dashboard stays current, but no money moves until you resume.",
      },
      {
        q: "What if my bank link breaks?",
        a: "ETAP notifies you and pauses routing for that account until the link is restored, so nothing moves on stale data. Reconnecting takes a moment through Plaid.",
      },
    ],
  },
  {
    id: "agencies",
    title: "Agencies",
    items: [
      {
        q: "How do agencies earn?",
        a: "For an agency referred user, the flat 0.5 percent fee is split evenly: 0.25 percent to the referring agency and 0.25 percent to the platform. The user still pays 0.5 percent total, never more.",
      },
      {
        q: "Can agencies change a client rate?",
        a: "Rates can be adjusted within consent guardrails. Clients are always notified and their acknowledgments are recorded before a change takes effect.",
      },
      {
        q: "What reporting do agencies get?",
        a: "Per client and consolidated reports, exportable as CSV and PDF, plus a commission earnings dashboard that reconciles to the transaction logs.",
      },
      {
        q: "Is client data separated?",
        a: "Yes. Each agency sees only its own clients. Client data is isolated to your agency.",
      },
    ],
  },
  {
    id: "fees",
    title: "Fees",
    items: [
      {
        q: "How much does ETAP cost?",
        a: "A flat 0.5 percent of each routed amount, computed per transaction. It is enforced as a hard ceiling in code, so you can never be charged more than 0.5 percent.",
      },
      {
        q: "Is there a monthly fee?",
        a: "No. There is no monthly subscription.",
      },
      {
        q: "Is there a setup fee?",
        a: "No. Setup is free.",
      },
      {
        q: "Do I pay a fee on money I do not route?",
        a: "No. The fee applies only to amounts that are actually routed toward taxes. Money that stays in your account is never charged.",
      },
    ],
  },
];
